import React, { useState } from 'react';
import { Pencil, Check, RotateCcw, Trash2 } from 'lucide-react';
import SortableHeader from '../../components/SortableHeader';
import RowActions from '../../components/RowActions';
import ActionItemEditModal from '../../components/ActionItemEditModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useCompleteTask, useReopenTask, useDeleteTask, errorMessage } from '../../api/queries';
import { useToast } from '../../context/ToastContext';

// The account's action items with the same row menu as the Action Items
// page: edit, mark complete or reopen, delete. The account page holds its
// tasks in plain state rather than the query cache, so each change reloads
// the account once the server has it.
function AccountTasksTab({
  account,
  sortedTasks,
  tasksSort,
  handleTasksSort,
  canDo,
  reloadAccount,
}) {
  const toast = useToast();
  const complete = useCompleteTask();
  const reopen = useReopenTask();
  const remove = useDeleteTask();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const canEdit = canDo('tasks', 'edit');
  const canDelete = canDo('tasks', 'delete');
  const hasActions = canEdit || canDelete;

  const handleComplete = async (task) => {
    try {
      await complete.mutateAsync(task.id);
      setError('');
      toast.undo(`“${task.title}” marked complete`, () => reopen.mutate(task.id, { onSuccess: () => reloadAccount() }));
      await reloadAccount();
    } catch (err) { setError(errorMessage(err, 'Failed to complete action item')); }
  };

  const handleReopen = async (task) => {
    try {
      await reopen.mutateAsync(task.id);
      setError('');
      toast.undo(`“${task.title}” reopened`, () => complete.mutate(task.id, { onSuccess: () => reloadAccount() }));
      await reloadAccount();
    } catch (err) { setError(errorMessage(err, 'Failed to reopen action item')); }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(deleting.id);
      setDeleting(null);
      setError('');
      toast.success('Action item deleted');
      await reloadAccount();
    } catch (err) { setError(errorMessage(err, 'Failed to delete action item')); }
  };

  // The account endpoint sends each task's account_id but not its name; the
  // editor shows the name, so it comes from the account we are on.
  const edit = (task) => setEditing({ ...task, account_name: task.account_name || account?.name });

  const rowActions = (task) => [
    canEdit && { key: 'edit', label: 'Edit', icon: Pencil, onClick: () => edit(task) },
    canEdit && task.status === 'Open' && { key: 'complete', label: 'Mark Complete', icon: Check, onClick: () => handleComplete(task) },
    canEdit && task.status === 'Completed' && { key: 'reopen', label: 'Reopen', icon: RotateCcw, onClick: () => handleReopen(task) },
    canEdit && canDelete && { divider: true },
    canDelete && { key: 'delete', label: 'Delete', icon: Trash2, variant: 'danger', onClick: () => setDeleting(task) },
  ];

  return (
    <div className="card account-tasks">
      <div className="card-header">
        <h3 className="card-title">Action Items</h3>
      </div>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="Action Item" field="title" sortField={tasksSort.field} sortDirection={tasksSort.direction} onSort={handleTasksSort} />
              <SortableHeader label="Due Date" field="due_date" sortField={tasksSort.field} sortDirection={tasksSort.direction} onSort={handleTasksSort} />
              <SortableHeader label="Assigned To" field="assigned_to_name" sortField={tasksSort.field} sortDirection={tasksSort.direction} onSort={handleTasksSort} />
              <SortableHeader label="Status" field="status" sortField={tasksSort.field} sortDirection={tasksSort.direction} onSort={handleTasksSort} />
              {hasActions && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {sortedTasks.length > 0 ? (
              sortedTasks.map(task => (
                <tr key={task.id}>
                  <td>
                    {canEdit ? (
                      <button type="button" className="task-title-link" onClick={() => edit(task)} title="Open this action item">{task.title}</button>
                    ) : task.title}
                  </td>
                  <td>
                    {task.due_date ? (
                      <span className={new Date(task.due_date) < new Date() && task.status === 'Open' ? 'text-danger' : ''}>
                        {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    ) : '-'}
                  </td>
                  <td>{task.assigned_to_name || '-'}</td>
                  <td>
                    <span className={`badge ${task.status === 'Completed' ? 'badge-success' : 'badge-warning'}`}>
                      {task.status}
                    </span>
                  </td>
                  {hasActions && (
                    <td>
                      <RowActions id={task.id} actions={rowActions(task)} />
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={hasActions ? 5 : 4} className="text-center text-secondary">No action items</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ActionItemEditModal task={editing} isOpen={!!editing} onClose={() => setEditing(null)} onSaved={reloadAccount} />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Action Item"
        message={deleting ? `Delete “${deleting.title}”?` : ''}
        confirmText="Delete"
      />
    </div>
  );
}

export default AccountTasksTab;
